/*
Copyright (C) 2026  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import { observer } from "mobx-react";
import * as React from "react";
import styled from "styled-components";

import { ThemePalette } from "@src/components/Theme";
import Button from "@src/components/ui/Button";
import Checkbox from "@src/components/ui/Checkbox";
import Dropdown from "@src/components/ui/Dropdowns/Dropdown";
import StatusIcon from "@src/components/ui/StatusComponents/StatusIcon";
import TextInput from "@src/components/ui/TextInput";
import guestAccessSource, { instanceKey } from "@src/sources/GuestAccessSource";
import notificationStore from "@src/stores/NotificationStore";
import wizardStore from "@src/stores/WizardStore";
import DomUtils from "@src/utils/DomUtils";

import WizardRemoteAccess from "./WizardRemoteAccess";

import type { Instance } from "@src/@types/Instance";
import type {
  DriverPlan,
  GuestAccess,
  GuestCheck,
  GuestCheckResult,
  WizardData,
} from "@src/@types/WizardData";

const OS_ITEMS = [
  { label: "Windows", value: "windows" },
  { label: "Linux", value: "linux" },
];
const LINUX_AUTH_ITEMS = [
  { label: "Password", value: "password" },
  { label: "SSH private key", value: "key" },
];
// Destinations whose Windows drivers Coriolis can install in the source VM.
// Destinations whose drivers Coriolis installs in Windows guests (see
// coriolis_common.guestprep.CATALOG). Azure needs no download: its Hyper-V
// drivers ship with Windows; preparation makes them start at boot.
const TARGET_NAMES: { [type: string]: string } = {
  aws: "AWS",
  nutanix: "Nutanix",
  azure: "Azure (Hyper-V)",
  ibm: "IBM Cloud (VirtIO)",
  vmware_vsphere: "VMware (LSI SAS, E1000E)",
  proxmox: "Proxmox VE (VirtIO)",
  "hyper-v": "Hyper-V (storvsc, IDE)",
  kubevirt: "OpenShift Virtualization (VirtIO)",
};

export const PREPARABLE_TARGETS = ["aws", "nutanix", "azure", "ibm", "vmware_vsphere", "proxmox", "hyper-v", "kubevirt"];

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: auto;
  padding-right: 8px;
`;
const Intro = styled.div`
  color: ${ThemePalette.grayscale[4]};
  margin-bottom: 24px;
  line-height: 20px;
`;
const SectionTitle = styled.div`
  font-size: 16px;
  margin: 24px 0 12px 0;
`;
const Table = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid ${ThemePalette.grayscale[1]};
`;
const Row = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid ${ThemePalette.grayscale[1]};
  gap: 16px;
`;
const Cell = styled.div<{ width?: number; grow?: boolean }>`
  ${props => (props.width ? `width: ${props.width}px;` : "")}
  ${props => (props.grow ? "flex-grow: 1;" : "")}
  overflow: hidden;
  text-overflow: ellipsis;
`;
const Fields = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px 32px;
`;
const KeyInput = styled.textarea`
  width: 420px;
  max-width: 100%;
  min-height: 96px;
  font-family: ui-monospace, monospace;
  font-size: 12px;
  padding: 8px;
  border: 1px solid ${ThemePalette.grayscale[2]};
  border-radius: 4px;
  resize: vertical;
`;
const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const Label = styled.div`
  font-size: 10px;
  font-weight: 500;
  color: ${ThemePalette.grayscale[3]};
  text-transform: uppercase;
`;
const Inline = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 24px 0 8px 0;
`;
const Summary = styled.div<{ ok: boolean }>`
  color: ${props => (props.ok ? ThemePalette.success : ThemePalette.alert)};
`;
const Result = styled.div`
  border: 1px solid ${ThemePalette.grayscale[2]};
  border-radius: 4px;
  margin-top: 16px;
  padding: 12px 16px;
`;
const ResultHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  margin-bottom: 8px;
`;
const Drivers = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 16px;
  border: 1px solid ${ThemePalette.grayscale[2]};
  border-radius: 4px;
  font-size: 13px;
`;
const DriverRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;
const Muted = styled.span`
  color: ${ThemePalette.grayscale[4]};
  font-size: 12px;
`;
const CheckRow = styled.div`
  display: flex;
  gap: 8px;
  padding: 6px 0;
`;
const CheckBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;
const Steps = styled.ol`
  margin: 6px 0 0 0;
  padding-left: 18px;
  color: ${ThemePalette.black};
`;
const Step = styled.li`
  margin-bottom: 6px;
`;
const Command = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  font-family: monospace;
  font-size: 12px;
  background: ${ThemePalette.grayscale[7]};
  padding: 6px 8px;
  border-radius: 4px;
  word-break: break-all;
`;
const AddressField = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
`;
const FixBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
  margin-top: 4px;
  padding: 8px 12px;
  background: ${ThemePalette.grayscale[7]};
  border-radius: 4px;
`;
const CopyLink = styled.span`
  color: ${ThemePalette.primary};
  cursor: pointer;
  font-family: sans-serif;
  flex-shrink: 0;
`;

const STATUS_ICON: { [status: string]: string } = {
  ok: "COMPLETED",
  warning: "WARNING",
  error: "ERROR",
  skipped: "CANCELED",
};

export const defaultGuestAccess = (): GuestAccess => ({
  osTypes: {},
  windows: { username: "", password: "", port: 5985, https: false },
  linux: { username: "", password: "", port: 22 },
  results: null,
  signature: null,
  manual: false,
});

const osTypeOf = (access: GuestAccess, instance: Instance): "windows" | "linux" =>
  access.osTypes[instanceKey(instance)] ||
  (instance.os_type === "windows" ? "windows" : "linux");

export const guestAccessSignature = (data: WizardData): string => {
  const access = data.guestAccess || defaultGuestAccess();
  const instances = data.selectedInstances || [];
  return JSON.stringify({
    target: data.target?.id,
    instances: instances.map(i => [instanceKey(i), osTypeOf(access, i)]).sort(),
    windows: access.windows,
    linux: access.linux,
    addresses: access.addresses || {},
  });
};

/** Whether the wizard may leave the "Guest access" page. */
export const guestAccessReady = (data: WizardData): boolean => {
  const access = data.guestAccess || defaultGuestAccess();
  const instances = data.selectedInstances || [];
  const hasWindows = instances.some(i => osTypeOf(access, i) === "windows");
  const preparable = PREPARABLE_TARGETS.includes(data.target?.type || "");
  if (!hasWindows || access.manual || !preparable) {
    return true;
  }
  if (!access.results || access.signature !== guestAccessSignature(data)) {
    return false;
  }
  return access.results.every(r => r.ready);
};

/** Windows guests present that Coriolis should prepare for the target. */
export const windowsGuests = (data: WizardData): string[] => {
  const access = data.guestAccess || defaultGuestAccess();
  return (data.selectedInstances || [])
    .filter(i => osTypeOf(access, i) === "windows")
    .map(instanceKey);
};

type Props = {
  wizardData: WizardData;
};
type State = {
  checking: boolean;
  // "<instance>:<check id>" of the fix being confirmed / applied
  confirmFix: string | null;
  fixing: string | null;
};

@observer
class WizardGuestAccess extends React.Component<Props, State> {
  state: State = { checking: false, confirmFix: null, fixing: null };

  get access(): GuestAccess {
    return this.props.wizardData.guestAccess || defaultGuestAccess();
  }

  update(changes: Partial<GuestAccess>) {
    wizardStore.updateData({ guestAccess: { ...this.access, ...changes } });
  }

  async handleVerify() {
    const data = this.props.wizardData;
    if (!data.source || !data.target || !data.selectedInstances) {
      return;
    }
    this.setState({ checking: true });
    const signature = guestAccessSignature(data);
    try {
      const response = await guestAccessSource.check({
        endpointId: data.source.id,
        instances: data.selectedInstances,
        access: this.access,
        targetPlatform: data.target.type,
      });
      this.update({ results: response.results, signature, drivers: response.drivers || null });
    } catch (err: any) {
      notificationStore.alert(
        `Guest access check failed: ${err?.data?.description || err?.message || err}`,
        "error",
      );
    } finally {
      this.setState({ checking: false });
    }
  }

  renderInstances() {
    const instances = this.props.wizardData.selectedInstances || [];
    return (
      <Table>
        {instances.map(instance => (
          <Row key={instanceKey(instance)}>
            <Cell grow>{instance.name}</Cell>
            <Cell width={170}>
              <Muted>Detected: {instance.os_type ? instance.os_type.charAt(0).toUpperCase() + instance.os_type.slice(1) : "unknown"}</Muted>
            </Cell>
            <Cell width={180}>
              <Dropdown
                width={160}
                items={OS_ITEMS}
                selectedItem={OS_ITEMS.find(
                  o => o.value === osTypeOf(this.access, instance),
                )}
                onChange={item =>
                  this.update({
                    osTypes: {
                      ...this.access.osTypes,
                      [instanceKey(instance)]: item.value,
                    },
                  })
                }
              />
            </Cell>
          </Row>
        ))}
      </Table>
    );
  }

  renderCredentials(os: "windows" | "linux") {
    const creds = this.access[os];
    const set = (field: string, value: any) =>
      this.update({ [os]: { ...creds, [field]: value } } as any);
    return (
      <Fields>
        <Field>
          <Label>Username</Label>
          <TextInput
            width="240px"
            value={creds.username}
            placeholder={os === "windows" ? "Administrator or DOMAIN\\user" : "e.g. ubuntu"}
            onChange={e => set("username", e.target.value)}
            required={os === "windows"}
            autoComplete="off"
          />
        </Field>
        {os === "linux" ? (
          <Field>
            <Label>Sign in with</Label>
            <Dropdown
              width={180}
              items={LINUX_AUTH_ITEMS}
              selectedItem={LINUX_AUTH_ITEMS.find(o => o.value === (creds.pkey !== undefined ? "key" : "password"))}
              onChange={item =>
                this.update({
                  linux: item.value === "key" ? { ...creds, pkey: creds.pkey || "", password: "" } : { ...creds, pkey: undefined },
                })
              }
            />
          </Field>
        ) : null}
        {os === "linux" && creds.pkey !== undefined ? (
          <Field>
            <Label>Private key</Label>
            <KeyInput
              value={creds.pkey}
              placeholder="-----BEGIN OPENSSH PRIVATE KEY----- (RSA, ed25519 or ECDSA, without a passphrase)"
              spellCheck={false}
              autoComplete="off"
              onChange={e => set("pkey", e.target.value)}
            />
          </Field>
        ) : (
          <Field>
            <Label>Password</Label>
            <TextInput
              width="240px"
              type="password"
              value={creds.password}
              onChange={e => set("password", e.target.value)}
              required={os === "windows"}
              autoComplete="new-password"
            />
          </Field>
        )}
        <Field>
          <Label>{os === "windows" ? "WinRM port" : "SSH port"}</Label>
          <TextInput
            width="100px"
            value={String(creds.port || "")}
            onChange={e => set("port", parseInt(e.target.value, 10) || undefined)}
          />
        </Field>
        {os === "windows" ? (
          <Field>
            <Label>WinRM over HTTPS</Label>
            <Inline>
              <Checkbox
                checked={Boolean(creds.https)}
                onChange={checked =>
                  this.update({
                    windows: { ...creds, https: checked, port: checked ? 5986 : 5985 },
                  })
                }
              />
              <Muted>port 5986</Muted>
            </Inline>
          </Field>
        ) : null}
      </Fields>
    );
  }

  renderStep(step: { text: string; command: string | null }, index: number) {
    return (
      <Step key={index}>
        {step.text}
        {step.command ? (
          <Command>
            <span style={{ flexGrow: 1 }}>{step.command}</span>
            <CopyLink
              onClick={() =>
                DomUtils.copyTextToClipboard(step.command!, "Command copied")
              }
            >
              Copy
            </CopyLink>
          </Command>
        ) : null}
      </Step>
    );
  }

  async handleFix(instance: string, check: GuestCheck) {
    const data = this.props.wizardData;
    if (!data.source || !check.fix) {
      return;
    }
    this.setState({ fixing: `${instance}:${check.id}` });
    try {
      const out = await guestAccessSource.fix({ endpointId: data.source.id, instance, fix: check.fix });
      notificationStore.alert(out.detail, "success");
      this.setState({ confirmFix: null });
      await this.handleVerify();
    } catch (err: any) {
      notificationStore.alert(
        `Could not apply the fix: ${err?.data?.description || err?.message || err}`,
        "error",
      );
    } finally {
      this.setState({ fixing: null });
    }
  }

  renderFix(instance: string, check: GuestCheck) {
    if (!check.fix || check.status === "ok") {
      return null;
    }
    const key = `${instance}:${check.id}`;
    const busy = this.state.fixing === key;
    if (this.state.confirmFix !== key) {
      return (
        <div>
          <Button
            secondary
            onClick={() => this.setState({ confirmFix: key })}
            disabled={this.state.checking || Boolean(this.state.fixing)}
          >
            {check.fix.label}
          </Button>
        </div>
      );
    }
    return (
      <FixBox>
        <span style={{ flexBasis: "100%" }}>{check.fix.confirm}</span>
        <Button onClick={() => this.handleFix(instance, check)} disabled={busy}>
          {busy ? "Applying…" : "Confirm"}
        </Button>
        <Button secondary onClick={() => this.setState({ confirmFix: null })} disabled={busy}>
          Cancel
        </Button>
      </FixBox>
    );
  }

  // The hypervisor reports no IP: the VM's address can be entered (and is then used to reach the guest).
  renderAddress(instance: string, check: GuestCheck) {
    const addresses = this.access.addresses || {};
    if (check.id !== "address" || (check.status === "ok" && !addresses[instance])) {
      return null;
    }
    return (
      <AddressField>
        <Label>IP address of the VM</Label>
        <TextInput
          width="200px"
          value={addresses[instance] || ""}
          placeholder="e.g. 172.16.10.50"
          onChange={e => this.update({ addresses: { ...addresses, [instance]: e.target.value } })}
          autoComplete="off"
          data-test-id="wizardGuestAccess-address"
        />
        <Muted>Then verify again.</Muted>
      </AddressField>
    );
  }

  renderCheck(check: GuestCheck, instance: string) {
    return (
      <CheckRow key={check.id}>
        <StatusIcon status={STATUS_ICON[check.status] || "WARNING"} />
        <CheckBody>
          <div>
            {check.title}
            {check.detail ? <Muted> — {check.detail}</Muted> : null}
          </div>
          {check.remediation.length && check.status !== "ok" ? (
            <Steps>{(check.remediation as any[]).map((s, i) => this.renderStep(s, i))}</Steps>
          ) : null}
          {this.renderAddress(instance, check)}
          {this.renderFix(instance, check)}
        </CheckBody>
      </CheckRow>
    );
  }

  renderDrivers(plan: DriverPlan) {
    const target = TARGET_NAMES[plan.target] || plan.target;
    return (
      <Drivers>
        <b>Drivers for {target}</b>
        {plan.inbox ? (
          <Muted>
            They ship with Windows; SwiftIQ makes the {plan.boot_driver} storage driver start at boot.
          </Muted>
        ) : (
          plan.packages.map(p => (
            <DriverRow key={p.id}>
              <span>
                {p.name}
                {p.version ? ` — ${p.version}` : ""}
              </span>
              <Muted>
                {p.source === "bundle"
                  ? "from the SwiftIQ driver bundle (checksum verified)"
                  : `downloaded from ${p.url}${p.problem ? ` (bundled copy ${p.problem})` : " (not in the bundle)"}`}
                {p.drivers.length ? ` · ${p.drivers.join(", ")}` : ""}
              </Muted>
            </DriverRow>
          ))
        )}
        {!plan.inbox ? <Muted>Boot storage driver: {plan.boot_driver}</Muted> : null}
      </Drivers>
    );
  }

  renderResult(result: GuestCheckResult) {
    const instance = (this.props.wizardData.selectedInstances || []).find(
      i => instanceKey(i) === result.instance,
    );
    const status = result.ready
      ? result.checks.some(c => c.status === "warning")
        ? "WARNING"
        : "COMPLETED"
      : "ERROR";
    return (
      <Result key={result.instance}>
        <ResultHeader>
          <StatusIcon status={status} />
          {instance?.name || result.instance}
          <Muted>
            {result.os_type}
            {result.address ? ` · ${result.address}` : ""}
            {result.required ? "" : " · optional for this target"}
          </Muted>
        </ResultHeader>
        {result.checks.map(c => this.renderCheck(c, result.instance))}
      </Result>
    );
  }

  render() {
    const data = this.props.wizardData;
    const instances = data.selectedInstances || [];
    const access = this.access;
    const target = data.target?.type || "";
    const hasWindows = instances.some(i => osTypeOf(access, i) === "windows");
    const hasLinux = instances.some(i => osTypeOf(access, i) === "linux");
    const stale = access.results && access.signature !== guestAccessSignature(data);
    const ready = guestAccessReady(data);
    const preparable = PREPARABLE_TARGETS.includes(target);
    const namesOf = (os: "windows" | "linux") =>
      instances.filter(i => osTypeOf(access, i) === os).map(i => i.name || instanceKey(i));

    return (
      <Wrapper>
        <Intro>
          {preparable
            ? `Windows VMs need ${TARGET_NAMES[target] || target.toUpperCase()} drivers to boot after migration. SwiftIQ installs or enables them in the running source VMs over WinRM before each sync, so enter an administrator account and verify access. Linux VMs are prepared automatically during deployment; their checks are informational.${target === "nutanix" ? " After deployment SwiftIQ also installs Nutanix Guest Tools with this login (Linux: a user with a password and sudo)." : ""}`
            : "SwiftIQ cannot install drivers for this destination automatically. Install them in Windows VMs yourself and confirm below."}
        </Intro>

        <SectionTitle>Selected VMs and guest OS</SectionTitle>
        {this.renderInstances()}

        {hasWindows && !access.manual && preparable ? (
          <>
            <SectionTitle>Windows login (WinRM) · {namesOf("windows").join(", ")}</SectionTitle>
            {this.renderCredentials("windows")}
          </>
        ) : null}
        {hasLinux ? (
          <>
            <SectionTitle>Linux login (SSH, optional) · {namesOf("linux").join(", ")}</SectionTitle>
            {this.renderCredentials("linux")}
          </>
        ) : null}

        {WizardRemoteAccess.supported() && instances.length ? (
          <>
            <SectionTitle>Remote access to the migrated VMs</SectionTitle>
            <WizardRemoteAccess
              wizardData={data}
              windowsVms={namesOf("windows")}
              linuxVms={namesOf("linux")}
            />
          </>
        ) : null}

        {hasWindows ? (
          <Actions>
            <Checkbox
              checked={access.manual || !preparable}
              disabled={!preparable}
              onChange={manual => this.update({ manual })}
            />
            <span>
              Drivers are already installed in the Windows VMs (skip automatic preparation)
            </span>
          </Actions>
        ) : null}

        <Actions>
          <Button
            onClick={() => this.handleVerify()}
            disabled={this.state.checking || (hasWindows && !access.manual && preparable && (!access.windows.username || !access.windows.password))}
          >
            {this.state.checking ? "Verifying…" : "Verify access"}
          </Button>
          {access.results && !stale ? (
            <Summary ok={ready}>
              {ready && hasWindows && (access.manual || !preparable)
                ? "Automatic driver installation is skipped, so the checks below are for information only."
                : ready
                  ? "All required checks passed."
                  : "Fix the failed checks below and verify again."}
            </Summary>
          ) : null}
          {stale ? <Muted>Settings changed since the last check. Verify again.</Muted> : null}
          {!access.results && hasWindows && !access.manual ? (
            <Muted>Verify access to continue.</Muted>
          ) : null}
        </Actions>

        {access.results && !stale && hasWindows && access.drivers ? this.renderDrivers(access.drivers) : null}
        {access.results && !stale ? access.results.map(r => this.renderResult(r)) : null}
      </Wrapper>
    );
  }
}

export default WizardGuestAccess;
